import type { OpenTab } from "../features/document-tabs"
import type { ChatReference, DocumentData } from "./resizable-layout.types"
import { fetchWithAuth, API_BASE_URL, IS_LOCAL_API } from "@/lib/api"
import { waitForAccessToken } from "@/lib/wait-for-access-token"
import {
  resolveStoragePathForFetch,
  storagePathProbeCandidates,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"
import { isChatConversationTabId } from "./document-viewer.utils"

/** Default [left%, main%, chat%] when both sidebars visible. Must sum to 100. */
export const DEFAULT_RESIZABLE_PANEL_TRIPLE = [20, 48, 32] as const

/**
 * Guard for legacy stored percents from the old 3% collapsed strip.
 * Not a live width - collapsed rails are flex siblings (`w-12`), not Panel percents.
 */
export const COLLAPSED_CHAT_STRIP_MAX_PCT = 5

/**
 * Effective expanded left column % for Panel defaultSize and restore after strip collapse.
 */
export function expandedLeftPanelPercent(
  panelSizes: number[],
  expandedLeftSize: number,
): number {
  if (expandedLeftSize > COLLAPSED_CHAT_STRIP_MAX_PCT) return expandedLeftSize
  const p = panelSizes[0]
  if (p !== undefined && p > COLLAPSED_CHAT_STRIP_MAX_PCT) return p
  return DEFAULT_RESIZABLE_PANEL_TRIPLE[0]
}

/**
 * Effective expanded chat column % for Panel defaultSize and restore after strip collapse.
 * Prefer expandedRightSize (snapshotted via getSize() on collapse) over panelSizes[2],
 * which is intentionally not updated while the chat strip is collapsed and may stay at default 32%.
 */
export function expandedChatPanelPercent(
  panelSizes: number[],
  expandedRightSize: number,
): number {
  if (expandedRightSize > COLLAPSED_CHAT_STRIP_MAX_PCT) return expandedRightSize
  const p = panelSizes[2]
  if (p !== undefined && p > COLLAPSED_CHAT_STRIP_MAX_PCT) return p
  return DEFAULT_RESIZABLE_PANEL_TRIPLE[2]
}

/**
 * Percent sizes for expandable panels only. Collapsed rails are flex siblings
 * outside PanelGroup, so remaining Panels must still sum to 100.
 */
export function expandableShellPanelPercents(
  leftExpanded: number,
  chatExpanded: number,
  opts: { sidebarInGroup: boolean; agentInGroup: boolean },
): { left?: number; main: number; chat?: number } {
  const left = opts.sidebarInGroup ? leftExpanded : undefined
  const chat = opts.agentInGroup ? chatExpanded : undefined
  if (left != null && chat != null) {
    return { left, main: Math.max(0, 100 - left - chat), chat }
  }
  if (left != null) {
    return { left, main: Math.max(0, 100 - left) }
  }
  if (chat != null) {
    return { main: Math.max(0, 100 - chat), chat }
  }
  return { main: 100 }
}

/**
 * Pixel width of the PanelGroup given the shell row and which icon rails sit
 * outside it as fixed siblings.
 */
export function shellPanelGroupWidthPx(
  shellRowWidthPx: number,
  opts: { sidebarRailPx: number; agentRailPx: number },
): number {
  return Math.max(1, shellRowWidthPx - opts.sidebarRailPx - opts.agentRailPx)
}

/**
 * Pixel floors copied from maater-ui-panel-collapse-layout.ts.
 * Left under 248px is the rail. Agent under 320px is the strip.
 * Canvas keeps 160px. The preview floor (280) is not in this panel group.
 */
export const SHELL_LEFT_FLOOR_PX = 248
export const SHELL_AGENT_FLOOR_PX = 320
export const SHELL_CANVAS_MIN_PX = 160

/** Gallery `canDock`: the frame can spare `floor` after the other column and the canvas. */
export function shellFrameCanDock(frameWidth: number, otherPx: number, floor: number): boolean {
  return frameWidth - otherPx - SHELL_CANVAS_MIN_PX >= floor
}

export type SidebarPinToggleDecision = {
  collapsed: boolean
  pinned: boolean
  /** Open the auto-hide flyout. Never docks and never changes the auto-hide flag. */
  openFlyout: boolean
}

/**
 * PanelLeft while auto-hide is on.
 * Collapsed with room: dock and pin. No room: stay on the rail and open the flyout.
 * Docked and pinned: collapse, clear the pin, and open the flyout (the pointer is
 * on the rail, and mounting the rail does not fire mouseenter).
 * Auto-hide off docks or undocks and leaves the pin clear. No room: stay collapsed.
 */
export function nextSidebarPinToggle(input: {
  collapsed: boolean
  autoHide: boolean
  pinned: boolean
  roomForLeft: boolean
}): SidebarPinToggleDecision {
  const { collapsed, autoHide, roomForLeft } = input
  if (autoHide) {
    if (!collapsed) {
      return { collapsed: true, pinned: false, openFlyout: true }
    }
    if (!roomForLeft) {
      return { collapsed: true, pinned: false, openFlyout: true }
    }
    return { collapsed: false, pinned: true, openFlyout: false }
  }
  if (!collapsed) {
    return { collapsed: true, pinned: false, openFlyout: false }
  }
  if (!roomForLeft) {
    return { collapsed: true, pinned: false, openFlyout: false }
  }
  return { collapsed: false, pinned: false, openFlyout: false }
}

export type ShellColumnWidthDecision = {
  docked: boolean
  /** Pixel width when docked. Restore uses the last expanded size, raised to the floor. */
  widthPx: number
}

/**
 * Snap a shell column while dragging.
 * Under the floor (or no room to dock) the column is the rail.
 * At or above the floor it stays docked at the dragged width.
 * Dragging the rail back out restores lastExpandedPx (the stored width, or the
 * pointer width when reversing a snap in the same gesture).
 */
export function resolveShellColumnWidth(input: {
  draggedPx: number
  floorPx: number
  docked: boolean
  lastExpandedPx: number
  roomForFloor: boolean
}): ShellColumnWidthDecision {
  const { draggedPx, floorPx, docked, lastExpandedPx, roomForFloor } = input
  if (docked) {
    if (draggedPx < floorPx) return { docked: false, widthPx: floorPx }
    return { docked: true, widthPx: draggedPx }
  }
  if (draggedPx >= floorPx && roomForFloor) {
    return { docked: true, widthPx: Math.max(floorPx, lastExpandedPx) }
  }
  return { docked: false, widthPx: floorPx }
}

/**
 * Keep a column's pixel width stable when the PanelGroup width changes
 * (collapsed rails joining/leaving as flex siblings).
 */
export function rebasePanelPercentForGroupWidthChange(
  percent: number,
  fromGroupWidthPx: number,
  toGroupWidthPx: number,
  opts?: { min?: number; max?: number },
): number {
  const min = opts?.min ?? 5
  const max = opts?.max ?? 95
  if (
    !Number.isFinite(percent) ||
    fromGroupWidthPx <= 0 ||
    toGroupWidthPx <= 0 ||
    fromGroupWidthPx === toGroupWidthPx
  ) {
    return percent
  }
  const px = (percent / 100) * fromGroupWidthPx
  return Math.min(max, Math.max(min, (px / toGroupWidthPx) * 100))
}

/**
 * Generic fetch with retry logic for 404 errors (indexing delays)
 * Used by all document fetching functions to handle cases where
 * documents are created but not yet indexed in the database
 *
 * For 404s: Only retries 1-2 times (for indexing delays), not 8 times
 * This prevents console spam when files are legitimately deleted
 */
export async function fetchWithRetry(
  url: string,
  options?: { maxRetries?: number; baseDelay?: number; retryOn404?: boolean; max404Retries?: number }
): Promise<Response> {
  const maxRetries = options?.maxRetries ?? 8; // For network errors
  const baseDelay = options?.baseDelay ?? 200;
  const retryOn404 = options?.retryOn404 ?? true;
  // Limit 404 retries to 1-2 attempts (for indexing delays only, not deleted files)
  const max404Retries = options?.max404Retries ?? 2;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
      const response = await fetchWithAuth(fullUrl);

      // For 404 errors, only retry a few times (document may be indexing)
      // Don't retry excessively for deleted files
      if (response.status === 404 && retryOn404 && attempt < max404Retries) {
        const delay = baseDelay * Math.pow(2, attempt); // 200ms, 400ms
        // Only log first retry to reduce console noise
        if (attempt === 0) {
          console.log(`[fetchWithRetry] Document not found (404), retrying once in ${delay}ms (document may be indexing)...`);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Return response (success, 404 after retries, or other error)
      return response;
    } catch (error) {
      // If this is the last attempt, throw
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // For network errors, retry with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      if (attempt < 3) {
        console.log(`[fetchWithRetry] Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error('Failed to fetch after all retries');
}

/**
 * Fetch document data from API with retry logic for indexing delays
 * Pure function - no side effects
 *
 * Retries 404 errors with exponential backoff to handle cases where
 * documents are created but not yet indexed in the database (e.g., after ingestion)
 */
export async function fetchDocumentData(
  documentId: string,
  options?: {
    maxRetries?: number
    retryDelay?: number
    workspaces?: WorkspaceRouteInfo[]
  }
): Promise<DocumentData | null> {
  // Skip fetching for special view IDs (home, list, view:*, basecamp, basecamp-*, integration-*, search-view)
  if (documentId === 'home' ||
      documentId === 'edit-history' ||
      documentId === 'view:edit-history' ||
      documentId === 'list' ||
      documentId === 'inbox' ||
      documentId === 'flows' ||
      documentId === 'graph' ||
      documentId === 'search-view' ||
      documentId === 'people-index' ||
      documentId.startsWith('person-view-') ||
      documentId === 'basecamp' ||
      documentId.startsWith('workspace-home-') ||
      documentId.startsWith('desk-home-') ||
      documentId.startsWith('chat-conversation-') ||
      documentId.startsWith('view:') ||
      documentId.startsWith('basecamp-') ||
      documentId.startsWith('gdrive-preview-') ||
      documentId.startsWith('integration-')) {
    return null;
  }

  const workspacesForFetch = options?.workspaces ?? []
  // Slug-shaped paths need workspace list to map to content_root; probing the slug
  // burns retries with 403s and can hang forceReadFromFile on cold deep links.
  if (documentId.includes('/') && workspacesForFetch.length === 0) {
    console.warn(
      '[fetchDocumentData] Skipping by-path until workspaces load:',
      documentId,
    )
    return null
  }

  const storagePath = documentId.includes('/')
    ? resolveStoragePathForFetch(documentId, workspacesForFetch)
    : documentId
  const pathCandidates = documentId.includes('/')
    ? storagePathProbeCandidates(storagePath.replace(/^\/+/, ''))
    : [documentId];

  try {
    if (!(await waitForAccessToken())) {
      console.warn('[fetchDocumentData] Authentication not ready (session still hydrating)')
      return null
    }

    for (let i = 0; i < pathCandidates.length; i++) {
      const candidate = pathCandidates[i];
      const forceFromFile = IS_LOCAL_API && documentId.includes('/') ? '&forceReadFromFile=true' : ''
      const apiPath = documentId.includes('/')
        ? `/api/documents/by-path?path=${encodeURIComponent(candidate)}${forceFromFile}`
        : `/api/documents/${candidate}`;
      const apiUrl = `${API_BASE_URL}${apiPath}`;

      let response = await fetchWithRetry(apiUrl, {
        maxRetries: options?.maxRetries ?? 8,
        baseDelay: options?.retryDelay ?? 200,
        retryOn404: true,
        max404Retries: i === 0 ? 2 : 0,
      });

      // Session hydration race: one retry after JWT is available.
      if (response.status === 401) {
        if (await waitForAccessToken(2000)) {
          response = await fetchWithRetry(apiUrl, {
            maxRetries: 2,
            baseDelay: options?.retryDelay ?? 200,
            retryOn404: true,
            max404Retries: i === 0 ? 2 : 0,
          })
        }
      }

      if (response.ok) {
        return await response.json();
      }

      if (response.status === 404) {
        if (IS_LOCAL_API && documentId.includes('/') && forceFromFile) {
          const dbPath = `/api/documents/by-path?path=${encodeURIComponent(candidate)}`
          const dbUrl = `${API_BASE_URL}${dbPath}`
          const dbResponse = await fetchWithRetry(dbUrl, {
            maxRetries: options?.maxRetries ?? 4,
            baseDelay: options?.retryDelay ?? 200,
            retryOn404: true,
            max404Retries: i === 0 ? 2 : 0,
          })
          if (dbResponse.ok) {
            return await dbResponse.json()
          }
        }
        if (i < pathCandidates.length - 1) continue;
        console.log(`[fetchDocumentData] Document not found (404): ${documentId} - file may have been deleted`);
        return null;
      }

      // Org-scoped APIs often return 403 for "not visible" (same as miss). Keep probing
      // folder-hub candidates (e.g. path.md 403 -> path/README.md).
      if (response.status === 403) {
        if (i < pathCandidates.length - 1) continue;
        console.warn(
          `[fetchDocumentData] Access denied (403) for ${documentId} - wrong workspace/org or doc not in cloud DB`
        );
        return null;
      }

      console.error(`[fetchDocumentData] Failed to fetch document: HTTP ${response.status}`);
      if (i < pathCandidates.length - 1) continue;
      return null;
    }

    return null;
  } catch (error) {
    // Only log non-404 errors
    if (!(error instanceof Error && error.message.includes('404'))) {
      console.error('[fetchDocumentData] Failed to fetch document after retries:', error);
    }
    return null;
  }
}

/**
 * Convert tab type to chat reference type
 * Pure function - no side effects
 */
export function convertTabTypeToReferenceType(
  tabType: OpenTab["type"]
): "work_spec" | "note" | "voice_memo" | "meeting" | "idea" | "research" {
  switch (tabType) {
    case "work-spec": return "work_spec"
    case "voice-memo": return "voice_memo"
    case "note": return "note"
    case "explorer": return "note" // Explorer tabs
    case "integration-basecamp": return "note" // Basecamp projects
    case "integration-basecamp-todo": return "note" // Basecamp todos
    case "integration-basecamp-todo-list": return "note" // Basecamp todo lists
    case "integration-basecamp-triage": return "note" // Basecamp weekly triage
    case "integration-voice-memos": return "voice_memo"
    case "integration-product-board": return "note" // Product Board
    default: return "note"
  }
}

/**
 * Get current tab slot ID for navigation
 * Pure function - no side effects
 */
export function getCurrentTabSlotId(
  activeTabId: string | null,
  openTabs: OpenTab[]
): string {
  if (!activeTabId) {
    return 'tab-main' // Fallback to unified main content
  }

  const currentTab = openTabs.find(tab => tab.id === activeTabId)

  if (!currentTab?.tabSlotId) {
    console.warn('[Navigation] Tab missing tabSlotId:', { activeTabId, currentTab })
    return 'tab-main' // Fallback to unified main content
  }

  return currentTab.tabSlotId
}

/**
 * One entry per tab id. Rows in both tab bars are keyed by id, and a duplicate key
 * makes React orphan stale rows in the DOM until the list remounts. A duplicate keeps
 * the first position and takes the latest entry's data. Returns the input array when clean.
 */
export function dedupeOpenTabsById(tabs: OpenTab[]): OpenTab[] {
  const indexById = new Map<string, number>()
  let result: OpenTab[] | null = null
  tabs.forEach((tab, i) => {
    const existing = indexById.get(tab.id)
    if (existing === undefined) {
      indexById.set(tab.id, result ? result.length : i)
      if (result) result.push(tab)
      return
    }
    if (!result) result = tabs.slice(0, i)
    result[existing] = tab
  })
  return result ?? tabs
}

/**
 * Generate a stable tab slot ID using timestamp
 * Pure function - no side effects
 */
export function generateTabSlotId(): string {
  return `tab-${Date.now()}`
}

/** Tab row id for explorer tabs. Must not reuse tabSlotId (pollers treat tab-* as document ids). */
export function generateExplorerTabId(): string {
  return `explorer-${Date.now()}`
}

/**
 * Determine if a document type should create a new tab
 * Pure function - no side effects
 */
export function shouldCreateNewTab(
  openInNewTab: boolean,
  isListView: boolean,
  isFromIntegrationTab: boolean,
  isGraphTab: boolean,
  isCurrentTabPinned: boolean
): boolean {
  return openInNewTab || isGraphTab || isListView || isFromIntegrationTab || isCurrentTabPinned
}

/**
 * Build a folder-scoped chat reference (path is the stable id).
 */
export function buildFolderChatReference(path: string, title?: string): ChatReference {
  const normalized = path.replace(/^\/+|\/+$/g, "")
  const displayTitle =
    title?.trim() ||
    normalized.split("/").filter(Boolean).pop() ||
    normalized
  return {
    id: normalized,
    title: displayTitle,
    type: "folder",
    path: normalized,
    kind: "folder",
  }
}

/** Chat chip for an open tab: explorer folders use path id; documents use tab id. */
export function chatReferenceFromOpenTab(
  tab: Pick<OpenTab, "id" | "type" | "filePath" | "explorerData">,
  title: string,
): ChatReference | null {
  if (tab.type === "explorer") {
    const folderPath = tab.explorerData?.folderPath?.trim()
    if (!folderPath) return null
    return buildFolderChatReference(folderPath, title)
  }
  const path = tab.filePath || (tab.id.includes("/") ? tab.id : undefined)
  return {
    id: tab.id,
    title,
    type: convertTabTypeToReferenceType(tab.type),
    path,
  }
}

/** Composer chips are inserted by the `add-to-chat` window event, not React state alone. */
export function dispatchFolderAddToChat(path: string, title?: string): ChatReference {
  const folderRef = buildFolderChatReference(path, title)
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("add-to-chat", {
        detail: {
          id: folderRef.id,
          title: folderRef.title,
          name: folderRef.title,
          path: folderRef.path,
          documentType: "folder",
          type: "folder",
          kind: "folder",
        },
      }),
    )
  }
  return folderRef
}

export function getDocumentTypeFromPath(path: string): "work_spec" | "note" | "voice_memo" | "meeting" | "idea" | "research" {
  if (path.includes('work-spec') || path.includes('work_spec')) {
    return "work_spec"
  } else if (path.includes('voice-memo') || path.includes('voice_memo')) {
    return "voice_memo"
  } else if (path.includes('meeting')) {
    return "meeting"
  } else if (path.includes('idea')) {
    return "idea"
  } else if (path.includes('research')) {
    return "research"
  }
  return "note"
}

/**
 * Get file path for a tab
 * Pure function - no side effects (but async due to API call)
 */
export async function getTabFilePath(tabId: string): Promise<string | null> {
  // If tabId is already a path, return it
  if (tabId.includes('/')) {
    return tabId
  }

  // Skip special tabs (align with fetchDocumentData where applicable)
  if (
    tabId === 'home' ||
    tabId === 'list' ||
    tabId === 'search-view' ||
    tabId === 'people-index' ||
    tabId.startsWith('person-view-') ||
    tabId === 'edit-history' ||
    tabId === 'view:edit-history' ||
    tabId === 'basecamp' ||
    tabId.startsWith('view:') ||
    tabId.startsWith('new-') ||
    tabId.startsWith('basecamp-') ||
    tabId.startsWith('integration-') ||
    tabId.startsWith('chat-conversation-') ||
    tabId.startsWith('workspace-home-') ||
    tabId.startsWith('desk-home-') ||
    isChatConversationTabId(tabId) ||
    tabId === 'graph'
  ) {
    return null
  }

  try {
    const response = await fetchWithAuth(`/api/documents/${tabId}`, {
      credentials: 'include'
    })
    if (response.ok) {
      const docData = await response.json()
      return docData.path || docData.filePath || docData.file_path || docData.relative_path || null
    }
  } catch (err) {
    console.error('[getTabFilePath] Failed to fetch:', err)
  }

  return null
}

